import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { 
  Clock,   
  ChevronRight, 
  Home,
  CheckCircle2,
  XCircle,
  Tag
} from "lucide-react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

const PickSlot = () => {
  const location = useLocation();
  const navigate = useNavigate(); 

  // Recover state from location.state or fallback to sessionStorage
  const navState = useMemo(() => {
    if (location.state && (location.state.vendorId || location.state.serviceTitle)) {
      try {
        sessionStorage.setItem("lastBookingContext", JSON.stringify(location.state));
      } catch (e) { /* ignore */ }
      return location.state;
    }
    try {
      const saved = sessionStorage.getItem("lastBookingContext");
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignore */ }
    return {};
  }, [location.state]);

  const { serviceTitle = "", vendorId = "", serviceId = "" } = navState;

  const initialType = navState.bookingType
    ? (String(navState.bookingType).toLowerCase().includes("video") ? "Video Call" : "Audio Call")
    : "Audio Call";

  const initialPrice = Number(
    navState.price ||
    (initialType === "Video Call" ? navState.videoPrice : navState.audioPrice) ||
    0
  );

  const [vendor, setVendor] = useState(null);
  const [appointmentType, setAppointmentType] = useState(initialType);
  const [totalPrice, setTotalPrice] = useState(initialPrice);
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState("Morning");
  const [selectedSlot, setSelectedSlot] = useState(null); 
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(false);
  const [endTime, setEndTime] = useState("");
  const [vendorBlockedDates, setVendorBlockedDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [description, setDescription] = useState("");

  // Coupon States
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponSuccess, setCouponSuccess] = useState("");
  const [discountValue, setDiscountValue] = useState(0);
  const [finalPrice, setFinalPrice] = useState(initialPrice);
  const [isFree, setIsFree] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [showOffers, setShowOffers] = useState(false);

  // Reset coupon state if base price changes
  useEffect(() => {
    setAppliedCoupon(null);
    setCouponSuccess("");
    setCouponError("");
    setDiscountValue(0);
    setFinalPrice(totalPrice);
    setIsFree(false);
  }, [totalPrice]);

  const handleApplyCoupon = async (codeToApply) => {
    const code = codeToApply || couponCode;
    if (!code || !code.trim()) {
      setCouponError("Please enter a coupon code.");
      return;
    }
    if (!totalPrice || Number(totalPrice) <= 0) {
      setCouponError("Please select a service or wait for pricing to load before applying a coupon.");
      return;
    }
    setCouponLoading(true);
    setCouponError("");
    setCouponSuccess("");
    try {
      const response = await axiosInstance.post("/users/coupons/apply", {
        code: code.trim().toUpperCase(),
        amount: Number(totalPrice),
      });
      if (response.data && response.data.valid) {
        setAppliedCoupon({
          code: response.data.code,
          _id: response.data.couponId,
          discountAmount: response.data.discountAmount,
          discountType: response.data.discountType,
        });
        setDiscountValue(response.data.discountValue);
        setFinalPrice(response.data.discountedAmount);
        setIsFree(response.data.isFree);
        setCouponSuccess(response.data.message || "Coupon applied successfully!");
        setCouponCode("");
      } else {
        setCouponError(response.data.message || "Invalid coupon code.");
      }
    } catch (err) {
      console.error("Apply coupon error:", err);
      setCouponError(err.response?.data?.message || "Failed to apply coupon.");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponSuccess("");
    setCouponError("");
    setDiscountValue(0);
    setFinalPrice(totalPrice);
    setIsFree(false);
  };

  const formatDateString = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const calculateEnd24h = (start24h, dur) => {
    const [h, m] = start24h.split(":").map(Number);
    const date = new Date();
    date.setHours(h, m + dur, 0, 0);
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  const calculateEnd12h = useCallback((start24h, dur) => {
    if (!start24h || !dur) return "";
    const [hour, minute] = start24h.split(":").map(Number);
    const date = new Date();
    date.setHours(hour, minute + dur, 0, 0);
    let endH = date.getHours();
    const endM = date.getMinutes();
    const ampm = endH >= 12 ? "pm" : "am";
    endH = endH % 12 || 12;
    return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")} ${ampm}`;
  }, []);

  useEffect(() => {
    if (selectedSlot) {
      setEndTime(calculateEnd12h(selectedSlot.start, duration));
    }
  }, [duration, selectedSlot, calculateEnd12h]);

  const fetchAvailableSlots = useCallback(async (dateObj) => {
    if (!vendorId || !dateObj) return;
    const dateStr = formatDateString(dateObj);
    setSlotsLoading(true);
    try {
      const res = await axiosInstance.get(`/users/pickslot/${vendorId}/availability?date=${dateStr}&segment=all`);
      setAvailableSlots(res.data.slots || []);
    } catch (err) {
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [vendorId]);

  const findMatchingService = useCallback((servicesList, targetTitle, targetServiceId) => {
    if (!servicesList || !servicesList.length) return null;
    if (targetServiceId) {
      const byId = servicesList.find(
        (s) => String(s._id) === String(targetServiceId) || String(s.serviceId) === String(targetServiceId)
      );
      if (byId) return byId;
    }
    const incomingSlug = (targetTitle || "").toLowerCase().replace(/[^a-z0-9]/g, '');
    return (
      servicesList.find((s) => {
        const dbSlug = (s.title || "").toLowerCase().replace(/[^a-z0-9]/g, '');
        return s.title === targetTitle || (incomingSlug && dbSlug === incomingSlug);
      }) || servicesList[0]
    );
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!vendorId) return;
      try {
        const [vendorRes, blockedRes, couponRes] = await Promise.all([
          axiosInstance.get(`/users/vendors/${vendorId}`),
          axiosInstance.get(`/users/pickslot/${vendorId}/blocked-dates`),
          axiosInstance.get("/users/coupons")
        ]);
        const vendorData = vendorRes.data.vendor;
        setVendor(vendorData);
        setVendorBlockedDates(blockedRes.data.blockedDates || []);
        setAvailableCoupons(couponRes.data.coupons || []);
        const service = findMatchingService(vendorData?.services, serviceTitle, serviceId);
        if (service) {
          const fetchedPrice = appointmentType === "Video Call" 
            ? (service.prices?.videoCall ?? 0) 
            : (service.prices?.audioCall ?? 0);
          setTotalPrice(fetchedPrice > 0 ? fetchedPrice : (initialPrice || 0));
        } else if (initialPrice > 0) {
          setTotalPrice(initialPrice);
        }
      } catch (err) { 
        console.error("fetchData error:", err);
        if (initialPrice > 0) setTotalPrice(initialPrice);
      }
    };
    fetchData();
  }, [vendorId, serviceTitle, serviceId, appointmentType, findMatchingService, initialPrice]);

  useEffect(() => {
    setSelectedSlot(null); 
    if (selectedDate) fetchAvailableSlots(selectedDate);
  }, [selectedDate, fetchAvailableSlots]);
const groupedSlots = useMemo(() => {
  const now = new Date();
  const isToday = formatDateString(selectedDate) === formatDateString(now);
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();

  return availableSlots.reduce((acc, slot) => {
    const [h, m] = slot.start.split(":").map(Number);

    // 1. If it's today, skip slots that have already started or passed
    if (isToday) {
      if (h < currentHour || (h === currentHour && m <= currentMinutes)) {
        return acc; 
      }
    }

    // 2. Assign to Morning/Afternoon/Evening
    let p = h >= 17 ? 'Evening' : h >= 12 ? 'Afternoon' : 'Morning';
    if (!acc[p]) acc[p] = [];
    acc[p].push(slot);
    return acc;
  }, { Morning: [], Afternoon: [], Evening: [] });
}, [availableSlots, selectedDate]); // Added selectedDate to dependencies

const handleBookNow = async () => {
  if (!vendor || !vendor.services || vendor.services.length === 0) {
    console.error("No services found in vendor object:", vendor);
    return alert("Service data not loaded yet. Please wait 2 seconds and try again.");
  }

  if (!selectedSlot) {
    return alert("Please select an available time slot before booking.");
  }

  try {
    setLoading(true);

    const finalService = findMatchingService(vendor.services, serviceTitle, serviceId);
    const detectedId = finalService?._id; 

    if (!detectedId) {
      setLoading(false);
      return alert("The service could not be matched. Please go back and select the service again.");
    }

    // 5. Build Payload
    const payload = {
      vendorId,
      serviceId: detectedId,
      date: formatDateString(selectedDate),
      start: selectedSlot.start,
      end: calculateEnd24h(selectedSlot.start, duration),
      type: appointmentType,
      couponCode: appliedCoupon ? appliedCoupon.code : undefined,
    };

    // console.log("✅ Success! Payload ready:", payload);

    // Proceed to backend
    const response = await axiosInstance.post("/users/bookings/initiate", payload);
    
    if (response.data && response.data.success) {
      if (response.data.isFree) {
        // Free booking — skip Razorpay completely and verify immediately
        try {
          await axiosInstance.post("/users/bookings/verify-payment", {
            razorpay_order_id: "FREE",
            razorpay_payment_id: null,
            razorpay_signature: null,
            bookingData: {
              ...payload,
              serviceTitle: serviceTitle,
              description: description,
              start: new Date(`${payload.date}T${payload.start}:00`).toISOString(),
              end: new Date(`${payload.date}T${payload.end}:00`).toISOString(),
              price: 0,
              originalPrice: response.data.originalPrice,
              discountValue: response.data.discountValue,
              couponCode: response.data.couponCode,
              couponId: response.data.couponId,
            }
          });
          navigate("/profile/my-bookings", { state: { success: true } });
        } catch (err) {
          console.error("Free booking verification error:", err);
          alert(err.response?.data?.message || "Failed to confirm free booking. Contact support.");
        } finally {
          setLoading(false);
        }
        return;
      }

      // Paid booking — proceed with Razorpay checkout
      const { orderId, amount, currency, razorpayKeyId } = response.data;

      const options = {
        key: razorpayKeyId,
        amount: amount, 
        currency: currency || "INR",
        name: "EasyMarg",
        description: `Booking for ${serviceTitle}`,
        order_id: orderId,
        handler: async (paymentRes) => {
          try {
            await axiosInstance.post("/users/bookings/verify-payment", {
              razorpay_order_id: paymentRes.razorpay_order_id,
              razorpay_payment_id: paymentRes.razorpay_payment_id,
              razorpay_signature: paymentRes.razorpay_signature,
              bookingData: {
                ...payload,
                serviceTitle: serviceTitle,
                description: description,
                start: new Date(`${payload.date}T${payload.start}:00`).toISOString(),
                end: new Date(`${payload.date}T${payload.end}:00`).toISOString(),
                price: response.data.finalPrice,
                originalPrice: response.data.originalPrice,
                discountValue: response.data.discountValue,
                couponCode: response.data.couponCode,
                couponId: response.data.couponId,
              }
            });
            navigate("/profile/my-bookings", { state: { success: true } });
          } catch (err) {
            console.error("Paid booking verification error:", err);
            alert("Payment success, but booking record failed. Contact support.");
          } finally {
            setLoading(false);
          }
        },
        modal: { ondismiss: () => setLoading(false) },
        theme: { color: "#134DA7" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    }
  } catch (err) {
    setLoading(false);
    console.error("API Error:", err.response?.data);
    alert(err.response?.data?.message || "Slot no longer available.");
  }
};
  const isDateBlocked = ({ date }) => vendorBlockedDates.includes(formatDateString(date));
  const isCurrentDateBlocked = vendorBlockedDates.includes(formatDateString(selectedDate));

  return (
    <>
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-outfit">
      {/* Solid Dark Header for Breadcrumbs */}
      <div className="bg-[#020617] pt-24 pb-16 px-6">
        <div className="container mx-auto max-w-6xl text-center">
          <nav className="flex items-center justify-center gap-2 text-sm font-medium text-slate-300">
            <button onClick={() => navigate('/home')} className="hover:text-white transition-colors flex items-center gap-1">
              <Home size={14} /> Home
            </button>
            <ChevronRight size={14} className="opacity-50" />
            <button onClick={() => navigate('/services')} className="hover:text-white transition-colors">
              Services
            </button>
            <ChevronRight size={14} className="opacity-50" />
            <button onClick={() => navigate(-1)} className="hover:text-white transition-colors capitalize">
              {vendor?.name || "Expert"}
            </button>
            <ChevronRight size={14} className="opacity-50" />
            <span className="text-white">Book Appointment</span>
          </nav>
        </div>
      </div>

      <div className="flex-grow pb-16">
        <div className="container mx-auto max-w-7xl px-6 -mt-8 relative z-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 space-y-6">
              <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-xl font-bold text-gray-800 mb-8 flex items-center gap-2">
                  <div className="w-1.5 h-6 bg-[#134DA7] rounded-full"></div>
                  Select Date & Time
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 items-start">
                  <div className="calendar-container">
                    <Calendar 
                      onChange={setSelectedDate} 
                      value={selectedDate} 
                      minDate={new Date()} 
                      tileDisabled={isDateBlocked}
                      tileClassName={({ date, view }) => {
                        if (view === 'month' && isDateBlocked({ date })) {
                          return 'blocked-date-tile';
                        }
                        return null;
                      }}
                      className="border-none rounded-2xl custom-calendar-ui"
                    />
                  </div>

                  <div className="space-y-8">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">Call Mode</label>
                      <div className="flex p-1.5 bg-gray-50 rounded-2xl border border-gray-100">
                        {["Audio Call", "Video Call"].map((type) => (
                          <button key={type} onClick={() => {
                            setAppointmentType(type);
                            const s = findMatchingService(vendor?.services, serviceTitle, serviceId);
                            if (s) setTotalPrice(type === "Audio Call" ? (s.prices?.audioCall || 0) : (s.prices?.videoCall || 0));
                          }} className={`flex-1 py-3 rounded-xl font-bold text-xs transition-all ${appointmentType === type ? "bg-white text-[#134DA7] shadow-sm" : "text-gray-400"}`}>{type}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">Time Category</label>
                      <div className="grid grid-cols-3 gap-2">
                        {["Morning", "Afternoon", "Evening"].map(p => (
                          <button key={p} disabled={isCurrentDateBlocked} onClick={() => setSelectedPeriod(p)} className={`py-3 rounded-xl border-2 font-bold text-[10px] uppercase transition-all ${selectedPeriod === p ? "border-[#134DA7] bg-blue-50 text-[#134DA7]" : "border-gray-50 bg-gray-50 text-gray-400 disabled:opacity-20"}`}>{p}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-10">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Available Slots</label>
                  {slotsLoading ? (
                    <div className="h-20 flex items-center justify-center bg-slate-50 rounded-2xl animate-pulse text-slate-400 font-bold">Loading...</div>
                  ) : (groupedSlots[selectedPeriod].length === 0 || isCurrentDateBlocked) ? (
                    <div className="h-20 flex items-center justify-center bg-slate-50 rounded-2xl text-slate-400 font-bold text-xs text-center px-4">
                      {isCurrentDateBlocked ? "Expert is Unavailable Today" : "No slots available in this category"}
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                      {groupedSlots[selectedPeriod].map((slot) => {
                        const isBooked = slot.isBooked === true;
                        const isSelected = selectedSlot?.start === slot.start;

                        return (
                          <button 
                            key={slot.start} 
                            onClick={() => !isBooked && setSelectedSlot(slot)} 
                            disabled={!slot.available || isBooked} 
                            className={`py-3 rounded-xl border-2 font-bold text-sm transition-all relative
                              ${isBooked 
                                ? "border-slate-100 bg-slate-100 text-slate-300 cursor-not-allowed booked-slot-pattern" 
                                : isSelected 
                                  ? "border-blue-600 bg-blue-600 text-white shadow-lg shadow-blue-200" 
                                  : "border-slate-50 bg-slate-50 text-slate-600 hover:border-blue-200"
                              }`}
                          >
                            {slot.label}
                            {isBooked && (
                              <span className="absolute bottom-1 left-0 w-full text-[7px] text-slate-400 uppercase tracking-tighter text-center">Booked</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Briefly describe your requirement..." className="w-full border-2 border-slate-50 bg-slate-50 rounded-2xl p-4 font-medium outline-none focus:bg-white transition-all resize-none" />
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-[#134DA7] rounded-3xl p-6 md:p-8 text-white shadow-sm sticky top-28">
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-6 opacity-90">Booking Summary</h3>
                <div className="space-y-6 mb-8">
                  <div className="flex justify-between items-center border-b border-white/10 pb-4">
                    <span className="text-sm font-semibold opacity-90">Duration</span>
                    <div className="flex items-center gap-3 bg-white/10 p-1.5 rounded-xl">
                      <button onClick={() => duration > 15 && setDuration(d => d - 15)} className="w-6 h-6 flex items-center justify-center bg-white/10 rounded-md hover:bg-white/20">-</button>
                      <span className="font-bold text-sm px-1">{duration}m</span>
                      <button onClick={() => setDuration(d => d + 15)} className="w-6 h-6 flex items-center justify-center bg-white/10 rounded-md hover:bg-white/20">+</button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/10 rounded-xl"><Clock size={20} /></div>
                    <div>
                      <p className="text-xs font-semibold uppercase opacity-70 mb-1">Selected Time</p>
                      <p className="font-bold text-base">{selectedSlot ? `${selectedSlot.label} - ${endTime}` : 'Pick a slot'}</p>
                    </div>
                  </div>
                </div>

                {/* Coupon Section */}
                <div className="border-t border-white/10 pt-6 mt-6 mb-8">
                  <label className="block text-xs font-semibold uppercase tracking-wider opacity-90 mb-3">Promo Code / Voucher</label>
                  
                  {appliedCoupon ? (
                    <div className="flex items-center justify-between bg-white/10 rounded-2xl p-3 border border-emerald-500/30 animate-fade-in">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        <div>
                          <p className="font-bold text-sm uppercase tracking-wider text-emerald-300">{appliedCoupon.code}</p>
                          <p className="text-[10px] opacity-90 mt-0.5">
                            {appliedCoupon.discountType === "percentage" 
                              ? `${appliedCoupon.discountAmount}% discount applied` 
                              : `₹${appliedCoupon.discountAmount} flat discount applied`}
                          </p>
                        </div>
                      </div>
                      <button 
                        onClick={handleRemoveCoupon} 
                        className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg transition-all text-xs font-bold uppercase tracking-wider"
                        title="Remove Coupon"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        placeholder="Enter Code"
                        className="flex-1 bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-sm font-semibold uppercase tracking-wider outline-none focus:border-white/30 placeholder-white/60 text-white"
                        onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                      />
                      <button
                        onClick={() => handleApplyCoupon()}
                        disabled={couponLoading || !couponCode.trim()}
                        className="bg-white text-[#134DA7] font-bold text-xs uppercase px-5 py-3 rounded-xl hover:bg-opacity-90 active:scale-95 disabled:opacity-50 disabled:scale-100 transition-all"
                      >
                        {couponLoading ? "Applying" : "Apply"}
                      </button>
                    </div>
                  )}
                  
                  {couponError && (
                    <p className="text-red-300 text-[10px] font-bold mt-2 flex items-center gap-1 animate-shake">
                      ⚠ {couponError}
                    </p>
                  )}
                  {couponSuccess && (
                    <p className="text-emerald-300 text-[10px] font-bold mt-2 flex items-center gap-1">
                      ✓ {couponSuccess}
                    </p>
                  )}

                  {/* View Offers Button */}
                  {availableCoupons && availableCoupons.length > 0 && (
                    <button 
                      onClick={() => setShowOffers(true)}
                      className="mt-4 text-xs font-semibold text-emerald-300 hover:text-emerald-200 transition-colors flex items-center gap-1.5 border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 rounded-lg"
                    >
                      <Tag size={14} /> View Available Offers ({availableCoupons.length})
                    </button>
                  )}

                  {/* Bill Summary Section */}
                <div className="border-t border-white/10 pt-6 mt-6 mb-8">
                  <h4 className="text-xs font-semibold uppercase tracking-wider opacity-90 mb-4">Bill Summary</h4>
                  <div className="bg-white/10 rounded-2xl p-5 space-y-4">
                    <div className="flex justify-between items-center text-sm">
                      <span className="opacity-80">Consultation Fee</span>
                      <span className="font-semibold">₹{totalPrice}</span>
                    </div>
                    
                    {appliedCoupon && (
                      <div className="flex justify-between items-center text-sm text-emerald-400">
                        <span>Coupon Discount</span>
                        <span className="font-semibold">-₹{discountValue}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center text-sm">
                      <span className="opacity-80">Platform Fee</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs line-through opacity-50">₹50</span>
                        <span className="font-semibold text-emerald-400">Free</span>
                      </div>
                    </div>

                    <div className="border-t border-white/10 pt-4 mt-2 flex justify-between items-center">
                      <span className="text-sm font-bold uppercase tracking-wider">Total Amount</span>
                      <span className="text-2xl font-bold">{isFree ? "FREE" : `₹${finalPrice || totalPrice}`}</span>
                    </div>
                  </div>
                </div>
                </div>

                <button onClick={handleBookNow} disabled={loading || !selectedSlot || isCurrentDateBlocked} className="w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wider bg-white text-[#134DA7] shadow-lg disabled:opacity-50 hover:bg-opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2">
                  {loading ? "Processing..." : !selectedSlot ? "Select a Time Slot" : isFree ? "Confirm Booking (Free) 🎉" : "Confirm & Pay"} <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      <style>{`
        .custom-calendar-ui { width: 100% !important; background: transparent !important; border: none !important; }
        .react-calendar__tile { padding: 18px 8px !important; font-size: 13px !important; font-weight: 700 !important; border-radius: 12px !important; }
        .react-calendar__tile--active { background: #134DA7 !important; color: white !important; }
        
        /* Style for Blocked Dates */
        .blocked-date-tile {
          background-color: #fee2e2 !important; 
          color: #ef4444 !important;            
          text-decoration: line-through;        
          cursor: not-allowed !important;
        }

        /* Style for Already Booked Slots */
        .booked-slot-pattern {
           background-image: repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(0,0,0,0.03) 5px, rgba(0,0,0,0.03) 10px);
        }

        /* Coupon and Shake Animations */
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.25s ease-out forwards;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-4px); }
          40%, 80% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }

        /* Custom scrollbar for coupon list */
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.3);
        }
      `}</style>

      {/* Offers Modal Overlay */}
      {showOffers && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-[2rem] w-full max-w-md shadow-2xl overflow-hidden font-outfit border border-gray-100">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Tag className="text-[#134DA7]" size={20} />
                Available Offers
              </h3>
              <button onClick={() => setShowOffers(false)} className="text-gray-400 hover:text-gray-600 transition-colors bg-white rounded-full p-1 shadow-sm border border-gray-100">
                <XCircle size={24} />
              </button>
            </div>
            
            <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4 custom-scrollbar bg-gray-50/30">
              {availableCoupons.map((coupon) => {
                const isCurrentApplied = appliedCoupon?.code === coupon.code;
                const isMinOrderNotMet = totalPrice < coupon.minOrderAmount;
                const isDisabled = coupon.alreadyUsed || coupon.limitReached;

                return (
                  <div 
                    key={coupon._id} 
                    className={`p-5 rounded-2xl border transition-all ${
                      isCurrentApplied 
                        ? "bg-emerald-50 border-emerald-200" 
                        : isDisabled || isMinOrderNotMet
                          ? "bg-gray-50 border-gray-100 opacity-60"
                          : "bg-white border-gray-200 hover:border-[#134DA7] hover:shadow-md cursor-pointer"
                    }`}
                    onClick={() => {
                       if (!isDisabled && !isMinOrderNotMet && !isCurrentApplied) {
                           handleApplyCoupon(coupon.code);
                           setShowOffers(false);
                       }
                    }}
                  >
                    <div className="flex justify-between items-start gap-4 mb-2">
                      <span className={`inline-block border border-dashed rounded-lg px-2.5 py-1 font-bold text-xs uppercase tracking-wider ${isCurrentApplied ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-gray-100 text-gray-600 border-gray-300'}`}>
                        {coupon.code}
                      </span>
                      {isCurrentApplied && <span className="text-emerald-600 font-bold text-[10px] uppercase tracking-wider flex items-center gap-1 bg-emerald-100 px-2 py-1 rounded-md"><CheckCircle2 size={12}/> Applied</span>}
                    </div>
                    
                    <p className="text-sm font-semibold text-gray-800 mb-1">
                      {coupon.description || `${coupon.discountType === "percentage" ? `${coupon.discountAmount}%` : `₹${coupon.discountAmount}`} off`}
                    </p>
                    
                    {coupon.minOrderAmount > 0 && (
                      <p className={`text-[10px] font-medium mt-2 ${isMinOrderNotMet ? "text-red-500" : "text-gray-500"}`}>
                        Min. Booking Amount: ₹{coupon.minOrderAmount}
                      </p>
                    )}
                    {coupon.alreadyUsed && (
                      <p className="text-red-500 text-[10px] font-semibold mt-1">
                        You already used this coupon
                      </p>
                    )}
                    {coupon.limitReached && (
                      <p className="text-red-500 text-[10px] font-semibold mt-1">
                        Coupon usage limit reached
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PickSlot;